import { Loader } from "@vds/loaders";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { useDispatch, useSelector } from "react-redux";
import { createBrowserRouter, createRoutesFromElements, Navigate, Route, RouterProvider, useLocation } from "react-router-dom";
import { fetchAllCameraAndFovsForRbac, fetchAllCompanionDevices } from "./api/DeviceServiceManager";
import { accountDetailsApi, accountFeaturesApi, invalidateTokenApi, validateSessionApi } from "./api/SessionServiceManager";
import { fetchSnowflakeSessionToken } from "./api/CortexServiceManager";
import { clearAuthStorage, setupResponseInterceptor } from "./global/axiosInterceptor";
import ErrorPage from "./global/components/ErrorHandling/ErrorPage";
import { PageLoadingFallback } from "./global/components/LoadingFallback";
import LicenseOveruseModal from "./global/components/Modals/LicenseOveruseModal";
import RoiModal from "./global/components/Modals/RoiModals";
import AccessDeniedModal from "./global/components/Modals/AccessDeniedModal";
import {
  COOKIE_ACCESS_TOKEN,
  COOKIE_ACTIVE_SESSION,
  COOKIE_DOMAIN,
  COOKIE_ID_TOKEN,
  COOKIE_IS_LOGIN,
  COOKIE_PATH,
  COOKIE_REFRESH_TOKEN,
  userLoggedIn
} from "./global/constants/cookieConstants";
import { startIdleTimer, stopIdleTimer } from "./global/idleTimeModal";
import {
  clearAllCookies,
  formatTimeRange,
  getCookie,
  getLicenseOveruseState,
  getSessionStorageData,
  refreshAllDeviceList
} from "./global/utility/Utils";
import { LICENSE_SKU_PATTERNS,
  SKU_TO_APP_MAPPING,
  LICENSE_ORIGIN,
  BASE_LICENSE_SKUS,
  isFeatureAccessible } from "./global/constants/licensesConstants";
import { ROLE_ERROR_MESSAGES, NO_LICENSE_MESSAGES } from "./global/constants/accessConstants";
import RootLayoutV2 from "./layouts/RootLayoutV2";
import SearchResult from "./pages/HistoricalSearch/SearchResult";
import {
  apiError,
  fetchCountryIdsRequest,
  fetchCVApp,
  fetchedUnPairedCameraDevices,
  getAllRoiListWithCvApps,
  selectedTimerData,
  resetApp,
  setRoleError,
  setUserActiveSession,
  showNotificationBanner,
  updateCompanionDeviceSite,
  updateDevices,
  updateWidgetConfig,
  userRoleAction
} from "./store/actions";
import { setNluEnabled } from "./store/actions/chatbot-actions";
import { fetchKnowiWidgets, storeWidgetList } from "./store/actions/knowi-actions";
import { complete_widgetList } from "./pages/Statistics/statisticsConstants";
import { fetchBillingAccountsLicenses, fetchLicenses } from "./store/actions/license-actions";
import HistoricalSearch from "./pages/HistoricalSearch";
// Removing lazy loading from dashboard
import Dashboard from "./pages/Dashboard";
import { STORAGE } from "./global/constants/storageConstants";
import { ROUTES } from "./global/constants/routesConstants";
import { cancelTokenRefresh } from "./global/proactiveTokenRefresh";
import { editRoles, getRoleByUser } from "./model/roleService";
import { CHART_TYPES } from "./pages/Statistics/statisticsConstants";

// Lazy-loaded page components for code splitting
// Dev mode uses direct imports for faster HMR and simpler debugging
const isLazyLoadDisabled = window.REACT_APP_IS_LOCAL === "true";
// Gates the Search routes to match the nav item in NavMenu.jsx
const isSearchEnabled = window.REACT_APP_HISTORICAL_SEARCH_ENABLED === "true";
// Gates the Enrolled Appearance routes
const isEaEnabled = window.REACT_APP_ENROLLED_APPEARANCE === "true";
// Conditional imports based on environment
const CameraInfo = isLazyLoadDisabled
  ? require("./features/cameraInfo").default
  : lazy(() => import("./features/cameraInfo"));
const BulkRule = isLazyLoadDisabled
  ? require("./pages/BulkRule").default
  : lazy(() => import("./pages/BulkRule"));
const CreateRule = isLazyLoadDisabled
  ? require("./pages/BulkRule/CreateRule").default
  : lazy(() => import("./pages/BulkRule/CreateRule"));
const CVApps = isLazyLoadDisabled
  ? require("./pages/CVApps").default
  : lazy(() => import("./pages/CVApps"));
const CreateCVApps = isLazyLoadDisabled
  ? require("./pages/CVApps/CreateCVApps").default
  : lazy(() => import("./pages/CVApps/CreateCVApps"));
const CameraOnboarding = isLazyLoadDisabled
  ? require("./pages/CameraOnboarding").default
  : lazy(() => import("./pages/CameraOnboarding"));
const CVAppSub = isLazyLoadDisabled
  ? require("./pages/CameraOnboarding/cvAppsManage/cvAppSub").default
  : lazy(() => import("./pages/CameraOnboarding/cvAppsManage/cvAppSub"));
const CVAppsLicenseType = isLazyLoadDisabled ? require("./pages/CameraOnboarding/cvAppsManage/cvAppsLicenseType").default : lazy(() => import("./pages/CameraOnboarding/cvAppsManage/cvAppsLicenseType"));
const StreamManualCalibration = isLazyLoadDisabled ? require("./pages/CameraOnboarding/streamManualCalibration").default : lazy(() => import("./pages/CameraOnboarding/streamManualCalibration"));
const CompanionDeviceInfo = isLazyLoadDisabled ? require("./pages/CompanionDeviceInfo").default : lazy(() => import("./pages/CompanionDeviceInfo"));
//const Dashboard = isLazyLoadDisabled ? require("./pages/Dashboard").default : lazy(() => import("./pages/Dashboard"));
const NewDevices = isLazyLoadDisabled ? require("./pages/Devices/NewDevices").default : lazy(() => import("./pages/Devices/NewDevices"));
const EditSite = isLazyLoadDisabled ? require("./pages/EditSites").default : lazy(() => import("./pages/EditSites"));
const CampusDetails = isLazyLoadDisabled ? require("./pages/EditSites/CampusDetails/CampusDetails").default : lazy(() => import("./pages/EditSites/CampusDetails/CampusDetails"));
const EventsInfo = isLazyLoadDisabled ? require("./pages/EventsInfo").default : lazy(() => import("./pages/EventsInfo"));
const ForgotPassword = isLazyLoadDisabled ? require("./pages/ForgotPassword").default : lazy(() => import("./pages/ForgotPassword"));
const IdleTimeModal = isLazyLoadDisabled ? require("./pages/IdleTimeModal").default : lazy(() => import("./pages/IdleTimeModal"));
const Incidents = isLazyLoadDisabled ? require("./pages/Incidents").default : lazy(() => import("./pages/Incidents"));
const Licenses = isLazyLoadDisabled ? require("./pages/Licenses").default : lazy(() => import("./pages/Licenses"));
const LicenseDetails = isLazyLoadDisabled ? require("./pages/Licenses/LicenseDetails").default : lazy(() => import("./pages/Licenses/LicenseDetails"));
const LicenseDetailsRoute = () => {
  const location = useLocation();
  return <LicenseDetails key={location.key} />;
};
const Location = isLazyLoadDisabled ? require("./pages/Location").default : lazy(() => import("./pages/Location"));
const Login = isLazyLoadDisabled ? require("./pages/Login").default : lazy(() => import("./pages/Login"));
const PasswordReset = isLazyLoadDisabled ? require("./pages/PasswordReset").default : lazy(() => import("./pages/PasswordReset"));
const AnnotationDraw = isLazyLoadDisabled ? require("./pages/Polygon").default : lazy(() => import("./pages/Polygon"));
const RegionOfInterest = isLazyLoadDisabled ? require("./pages/RegionOfInterest").default : lazy(() => import("./pages/RegionOfInterest"));
const CreateRole = isLazyLoadDisabled ? require("./pages/Roles/CreateRole").default : lazy(() => import("./pages/Roles/CreateRole"));
const EditRoleTemplate = isLazyLoadDisabled ? require("./pages/Roles/EditRoleTemplate").default : lazy(() => import("./pages/Roles/EditRoleTemplate"));
const Roles = isLazyLoadDisabled ? require("./pages/Roles/Roles").default : lazy(() => import("./pages/Roles/Roles"));
const Alerts = isLazyLoadDisabled ? require("./pages/Rules").default : lazy(() => import("./pages/Rules"));
const CreateRuleSummary = isLazyLoadDisabled ? require("./pages/Rules/CreateRuleSummary").default : lazy(() => import("./pages/Rules/CreateRuleSummary"));
const RuleDetails = isLazyLoadDisabled ? require("./pages/Rules/RuleDetails").default : lazy(() => import("./pages/Rules/RuleDetails"));
const Settings = isLazyLoadDisabled ? require("./pages/Settings").default : lazy(() => import("./pages/Settings"));
const Sites = isLazyLoadDisabled ? require("./pages/Sites/Sites").default : lazy(() => import("./pages/Sites/Sites"));
const StatDataSource = isLazyLoadDisabled ? require("./pages/Statistics/StatDataSource").default : lazy(() => import("./pages/Statistics/StatDataSource"));
const StatisticsComponent = isLazyLoadDisabled ? require("./pages/Statistics/StatisticsComponent").default : lazy(() => import("./pages/Statistics/StatisticsComponent"));
const Support = isLazyLoadDisabled ? require("./pages/Support/Support").default : lazy(() => import("./pages/Support/Support"));
const UserDetails = isLazyLoadDisabled ? require("./pages/UserDetails").default : lazy(() => import("./pages/UserDetails"));
const UserGroups = isLazyLoadDisabled ? require("./pages/UserGroups").default : lazy(() => import("./pages/UserGroups"));
const UserGroupsDetails = isLazyLoadDisabled ? require("./pages/UserGroups/UserGroupsDetails").default : lazy(() => import("./pages/UserGroups/UserGroupsDetails"));
const UserRegistration = isLazyLoadDisabled ? require("./pages/UserGroups/UserRegistration").default : lazy(() => import("./pages/UserGroups/UserRegistration"));
const Users = isLazyLoadDisabled ? require("./pages/Users").default : lazy(() => import("./pages/Users"));
const Assistant = isLazyLoadDisabled ? require("./pages/Assistant").default : lazy(() => import("./pages/Assistant"));
const AssociatedStreams = isLazyLoadDisabled ? require("./pages/AssociatedStreams/AssociatedStreams").default : lazy(() => import("./pages/AssociatedStreams/AssociatedStreams"));
const EAList = isLazyLoadDisabled ? require("./pages/EnrolledAppearance/EAList/EAList").default : lazy(() => import("./pages/EnrolledAppearance/EAList/EAList"));
const EACreate = isLazyLoadDisabled ? require("./pages/EnrolledAppearance/EACreate/EACreate").default : lazy(() => import("./pages/EnrolledAppearance/EACreate/EACreate"));
const UserSettings = isLazyLoadDisabled ? require("./pages/UserSettings/UserSettings").default : lazy(() => import("./pages/UserSettings/UserSettings"));

function NewApp() {
  const dispatch = useDispatch();
  const activeSessionCookie = getCookie(COOKIE_ACTIVE_SESSION);
  const { activeSession: reducerActiveSession } = useSelector((state) => state.sessionReducer.activeSession);
  const [loginStatus, setLoginStatus] = useState(userLoggedIn.LOADING);
  // const userRole = JSON.parse(sessionStorage.getItem("selectedRole"));
  const licenseInfo = useSelector((state) => state.licenseReducer.licenses);
  const otherAppLicenses = useSelector((state) => state.licenseReducer.otherAppLicenses);
  const licensesLoaded = useSelector((state) => state.licenseReducer.licensesLoaded);
  const licensesLoadedOrigin = useSelector((state) => state.licenseReducer.licenseLoadedOrigin);
  const billingAccountsLoaded = useSelector((state) => state.licenseReducer.billingAccountsLoaded);
  const billingAccounts = useSelector((state) => state.licenseReducer.billingAccounts);
  const ecpdId = useSelector((state) => state.licenseReducer.ecpdId);
  const ecpdAccountName = useSelector((state) => state.licenseReducer.ecpdAccountName);
  const hasLicenseForbiddenError = useSelector((state) => state.errorReducer.hasLicenseForbiddenError);
  const isV1OnlyAccount = useSelector((state) => state.licenseReducer.isV1OnlyAccount);
  const isNluEnabled = useSelector((state) => state?.chatbot?.isNluEnabled);
  const roleError = useSelector((state) => state.errorReducer.roleError);
  const [showOveruseModal, setShowOveruseModal] = useState(false);
  const [showNoLicenseModal, setShowNoLicenseModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [accountEmail, setAccountEmail] = useState("");
  const nonEssentialApisFired = useRef(false);
  const statisticsCharts = useSelector((state) => state.userAppSettingsReducer.StatisticsCharts);
  const dispatchWithPromise = () => {
    return new Promise((resolve) => {
      dispatch(fetchCountryIdsRequest(resolve));
    });
  };
  const [token, setToken, removeToken] = useCookies([COOKIE_ACCESS_TOKEN, COOKIE_ID_TOKEN, COOKIE_REFRESH_TOKEN, COOKIE_IS_LOGIN]);

  const isAccountCancelled = useMemo(() => {
    if (!Array.isArray(licenseInfo) || licenseInfo.length === 0) {
      return false;
    }

    const baseLicenses = licenseInfo.filter((lic) => BASE_LICENSE_SKUS.includes(lic?.sku));
    if (baseLicenses.length === 0) {
      return false;
    }

    return baseLicenses.every((lic) => lic.state !== "active");
  }, [licenseInfo]);

  const overuseLicense = useMemo(() => {
    if (!Array.isArray(licenseInfo)) return null;
    return licenseInfo.find((lic) => lic.overusestartedon && lic.overusestartedon !== "0001-01-01T00:00:00Z");
  }, [licenseInfo]);

  const hasValidLicense = useMemo(() => {
    // licenseInfo is already vision-only (filtered at the saga source), so no
    // need to re-check sku membership here.
    return Array.isArray(licenseInfo) && licenseInfo.length > 0 && licenseInfo.some((l) => l?.licenses >= 1);
  }, [licenseInfo]);

  const shouldShowNoLicenseModal = useMemo(
    () => hasLicenseForbiddenError || !hasValidLicense,
    [hasLicenseForbiddenError, hasValidLicense]
  );

  useEffect(() => {
    if (loginStatus !== userLoggedIn.SUCCESS) {
      return;
    }

    if (!reducerActiveSession) {
      return;
    }

    // Wait for both licenses and billing accounts to be loaded
    if (!licensesLoaded || !billingAccountsLoaded) {
      return;
    }

    if (shouldShowNoLicenseModal && licensesLoadedOrigin === LICENSE_ORIGIN.NEW_APP) {
      setShowNoLicenseModal(true);
      dispatch(showNotificationBanner({ showNotification: false }));
      return;
    }

    if (overuseLicense) {
      const status = getLicenseOveruseState(overuseLicense.overusestartedon);
      if (status === "expired") {
        setShowOveruseModal(true);
        dispatch(showNotificationBanner({ showNotification: false }));
        return;
      } else if (status === "grace_period") {
        dispatch(
          showNotificationBanner({
            type: "License overuse",
            priority: "warning",
            showNotification: true,
            isDismissible: false
          })
        );
        setShowOveruseModal(false);
        return;
      }
    }
    if (isAccountCancelled) {
      dispatch(
        showNotificationBanner({
          type: "Account cancelled",
          priority: "info",
          showNotification: true,
          isDismissible: false
        })
      );
    } else {
      dispatch(showNotificationBanner({ showNotification: false }));
    }
    setShowOveruseModal(false);
  }, [shouldShowNoLicenseModal, isAccountCancelled, overuseLicense, licensesLoaded, billingAccountsLoaded, loginStatus, reducerActiveSession, dispatch]);

  // Fire non-essential APIs only after access is fully confirmed (role ok + license ok).
  // Blocked for denied users — prevents wasted calls to cvApp, hierarchy, devices, etc.
  // License validity is computed directly (not from showNoLicenseModal state) to avoid a
  // React render-cycle timing issue where both this effect and the license check effect run
  // on the same render — showNoLicenseModal setState is queued but not yet applied.
  useEffect(() => {
    if (nonEssentialApisFired.current) return;
    if (loginStatus !== userLoggedIn.SUCCESS) return;
    if (!licensesLoaded || !billingAccountsLoaded) return;
    if (roleError) return;
    if (shouldShowNoLicenseModal) return;

    const sessionActive = reducerActiveSession ?? JSON.parse(activeSessionCookie);
    if (!sessionActive?.accountId) return;

    nonEssentialApisFired.current = true;

    dispatch(fetchCVApp());
    dispatch(getAllRoiListWithCvApps());
    if (statisticsCharts?.chart === CHART_TYPES.KNOWI) {
      dispatch(fetchKnowiWidgets());
    } else {
      dispatch(storeWidgetList(complete_widgetList));
    }
    handleUnpairApis();

    // Check NLU feature flag for Cortex AI chatbot (non-blocking)
    checkNluFeatureFlag(sessionActive);

    // Heavy chain: countryIds → getHierarchy (maxdepth=8) → getUserSites → devices + cameras
    dispatchWithPromise().then(() => {
      return Promise.all([
        fetchAllCompanionDevices(sessionActive).then((companionDevices) => {
          dispatch(updateCompanionDeviceSite(companionDevices.companiondevices));
          dispatch(fetchedUnPairedCameraDevices(companionDevices.discoveredCameraList));
        }),
        fetchAllCameraAndFovsForRbac(sessionActive).then((cameraAndFovDevices) => {
          dispatch(
            updateDevices({
              cameraList: cameraAndFovDevices?.cameras,
              fovList: cameraAndFovDevices?.fovs
            })
          );
        })
      ])
        .then(() => dispatch(apiError(false)))
        .catch((error) => {
          console.error("Error loading device data:", error);
          dispatch(apiError(true));
        });
    });
  }, [licensesLoaded, billingAccountsLoaded, shouldShowNoLicenseModal, roleError, loginStatus]);

  const handleManageLicenses = () => {
    setShowOveruseModal(false);
    router.navigate("/licenses", { replace: true });
  };

  const handleAccessDeniedLogout = async () => {
    setIsLoggingOut(true);
    try {
      let sessionActive = reducerActiveSession;
      if (!sessionActive && activeSessionCookie) {
        try {
          sessionActive = JSON.parse(activeSessionCookie);
        } catch (e) {
          sessionActive = null;
        }
      }
      setShowNoLicenseModal(false);
      cancelTokenRefresh();
      stopIdleTimer();
      await invalidateTokenApi(sessionActive).catch(() => {});
      dispatch(resetApp());
      clearAllCookies();
      sessionStorage.clear();
      localStorage.clear();
      window.location.replace(window.location.origin + ROUTES.LOGIN);
    } catch (error) {
      console.error("Error during logout:", error);
      window.location.replace(window.location.origin + ROUTES.LOGIN);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleAccessDeniedOk = () => {
    window.location.replace(window.REACT_APP_DOMAIN_SMARTVISION_SOLUTIONS);
  };

  useEffect(() => {
    startIdleTimer();
  }, []);

  // CWE-367 (TOCTOU): Session is checked at effect entry but used across multiple async operations.
  // If logout fires (reducerActiveSession → null) while async chain is running, React cleans up
  // this effect, setting cancelled=true. Every .then() callback checks this flag before dispatching
  // to Redux, preventing post-logout data (devices, cameras, licenses) from reaching the store.
  // Also prevents the double-dispatch race from Login (partial session → full session): the first
  // effect run is cancelled when the second dispatch arrives, avoiding stale accountId in API calls.
  useEffect(() => {
    let cancelled = false;

    if (reducerActiveSession) {
      const cookiesToDelete = [COOKIE_ACCESS_TOKEN, COOKIE_ID_TOKEN, COOKIE_REFRESH_TOKEN, COOKIE_IS_LOGIN]; // list of cookie names to remove
      cookiesToDelete.forEach((cookie) => {
        removeToken(cookie, { path: COOKIE_PATH, domain: COOKIE_DOMAIN });
      });
      const sessionActive = reducerActiveSession != null ? reducerActiveSession : JSON.parse(activeSessionCookie);

      // Login.jsx dispatches a partial session (no accountId) immediately after tokenApi
      // to seed the axios interceptor with the Bearer token. The complete session with
      // accountId is dispatched shortly after by proceedWithoutError. Skip API calls here
      // to avoid duplicating accountDetails/role/license calls that proceedWithoutError will trigger.
      if (!sessionActive.accountId) {
        return () => { cancelled = true; };
      }

      // Check if accountDetails already exists in sessionStorage to avoid duplicate API call
      const userInfo = getSessionStorageData(STORAGE.ACCOUNT_DETAILS);

      // PROGRESSIVE RENDERING: Show app immediately, don't wait for data
      setLoginStatus(userLoggedIn.SUCCESS);

      if (userInfo) {
        const applyAccountDetails = (details) => {
          setAccountEmail(details?.email || "");
          const widgetTimeRange = details?.customdata?.widgettimerange ? details.customdata.widgettimerange : 10 * 60 * 1000;
          const widgetConfig = details?.customdata?.widgetconfig ? details.customdata.widgetconfig : [];
          dispatch(updateWidgetConfig(widgetConfig));
          dispatch(selectedTimerData(formatTimeRange(widgetTimeRange) || "Last 10 min"));
        };

        const isFreshLogin = sessionStorage.getItem(STORAGE.FRESH_LOGIN) === "true";
        if (isFreshLogin) {
          // Fresh login — accountDetails just set by Login.jsx, use it directly (no re-fetch)
          sessionStorage.removeItem(STORAGE.FRESH_LOGIN);
          applyAccountDetails(userInfo);
        } else {
          // Page reload — re-fetch to get latest account details from server
          accountDetailsApi(sessionActive.access_token)
            .then((accountDetails) => {
              if (cancelled) return;
              sessionStorage.setItem(STORAGE.ACCOUNT_DETAILS, JSON.stringify(accountDetails));
              sessionStorage.setItem(STORAGE.ACCOUNT_ID, accountDetails.billingaccountid);
              applyAccountDetails(accountDetails);
            })
            .catch((error) => {
              if (cancelled) return;
              console.error("Failed to fetch account details:", error);
              dispatch(selectedTimerData("Last 10 min"));
            });
        }

        dispatch(fetchBillingAccountsLicenses());
        dispatch(fetchLicenses({"accountId": sessionActive.accountId, "origin":LICENSE_ORIGIN.NEW_APP}));

        // Re-fetch role + permissions only if either is missing in sessionStorage.
        // Skips duplicate calls when Login.jsx already set them on fresh login.
        // Recovers if either was missing (e.g. editRoles failed at login, or manual storage clear).
        const existingRole = getSessionStorageData(STORAGE.SELECTED_ROLE);
        const existingPermissions = getSessionStorageData(STORAGE.ROLE_PERMISSIONS_LIST);
        if (!existingRole || !existingPermissions) {
          getRoleByUser(sessionActive, userInfo.id)
            .then((roleByUser) => {
              if (cancelled) return null;
              if (!roleByUser?.length) {
                const errorMsg = roleByUser === null
                  ? ROLE_ERROR_MESSAGES.SERVICE_ERROR(userInfo?.email)
                  : ROLE_ERROR_MESSAGES.NO_ROLE_ASSIGNED(userInfo?.email);
                dispatch(setRoleError(errorMsg));
                return null;
              }
              dispatch(userRoleAction(roleByUser[0].name));
              sessionStorage.setItem(STORAGE.SELECTED_ROLE, JSON.stringify(roleByUser[0].name));
              return editRoles(sessionActive, roleByUser[0].id);
            })
            .then((rolePermissionsList) => {
              if (cancelled || !rolePermissionsList) return;
              sessionStorage.setItem(STORAGE.ROLE_PERMISSIONS_LIST, JSON.stringify(rolePermissionsList));
            })
            .catch((err) => {
              if (cancelled) return;
              console.error("Failed to refresh permissions:", err);
            });
        }
      } else {
        // No cached data (page reload without sessionStorage) — fetch accountDetails first,
        // then fire role + license in parallel so a role failure doesn't block the license check.
        accountDetailsApi(sessionActive.access_token)
          .then((accountDetails) => {
            if (cancelled) return;
            if (!accountDetails || accountDetails.error) {
              setLoginStatus(userLoggedIn.FAILED);
              return;
            }

            sessionStorage.setItem(STORAGE.ACCOUNT_DETAILS, JSON.stringify(accountDetails));
            sessionStorage.setItem(STORAGE.ACCOUNT_ID, accountDetails.billingaccountid);

            const widgetTimeRange = accountDetails?.customdata?.widgettimerange ?? 10 * 60 * 1000;
            const widgetConfig = accountDetails?.customdata?.widgetconfig ?? [];
            setAccountEmail(accountDetails?.email || "");
            dispatch(updateWidgetConfig(widgetConfig));
            dispatch(selectedTimerData(formatTimeRange(widgetTimeRange) || "Last 10 min"));

            // License + billing fired immediately — independent of role result
            dispatch(fetchBillingAccountsLicenses());
            dispatch(fetchLicenses({"accountId": accountDetails.billingaccountid, "origin":LICENSE_ORIGIN.NEW_APP}));

            // Role check — runs in parallel, failure dispatches roleError but doesn't block license
            getRoleByUser(sessionActive, accountDetails.id)
              .then((roleByUser) => {
                if (cancelled) return null;
                if (!roleByUser?.length) {
                  const errorMsg = roleByUser === null
                    ? ROLE_ERROR_MESSAGES.SERVICE_ERROR(accountDetails?.email)
                    : ROLE_ERROR_MESSAGES.NO_ROLE_ASSIGNED(accountDetails?.email);
                  dispatch(setRoleError(errorMsg));
                  return null;
                }
                dispatch(userRoleAction(roleByUser[0].name));
                sessionStorage.setItem(STORAGE.SELECTED_ROLE, JSON.stringify(roleByUser[0].name));
                return editRoles(sessionActive, roleByUser[0].id);
              })
              .then((rolePermissionsList) => {
                if (cancelled || !rolePermissionsList) return;
                sessionStorage.setItem(STORAGE.ROLE_PERMISSIONS_LIST, JSON.stringify(rolePermissionsList));
              })
              .catch((err) => {
                if (cancelled) return;
                console.error("Role check failed:", err);
                dispatch(setRoleError(ROLE_ERROR_MESSAGES.SERVICE_ERROR()));
              });
          })
          .catch((error) => {
            if (cancelled) return;
            console.error("Failed to fetch account details:", error);
            setLoginStatus(userLoggedIn.FAILED);
          });
      }
    } else if (!reducerActiveSession && !activeSessionCookie) {
      setLoginStatus(userLoggedIn.FAILED);
    }

    return () => {
      cancelled = true;
    };
  }, [reducerActiveSession]);

  const handleUnpairApis = async () => {
    await refreshAllDeviceList(1, reducerActiveSession ? reducerActiveSession : JSON.parse(activeSessionCookie));
  };

  const checkNluFeatureFlag = (sessionActive) => {
    if (sessionActive.userId && sessionActive.accountId) {
      accountFeaturesApi(sessionActive.userId, sessionActive.accountId)
        .then((resp) => {
          const isEnabled = resp?.isNluEnabled === true;
          dispatch(setNluEnabled(isEnabled));
          if (isEnabled) {
            fetchSnowflakeSessionToken(sessionActive.accountId).catch(() => {});
          }
        })
        .catch(() => dispatch(setNluEnabled(false)));
    }
  };

  // CWE-362 (Race Condition): Two concurrent browser tabs share COOKIE_ACTIVE_SESSION.
  // Tab B can start validateSessionApi before Tab A's logout invalidates the cookie server-side,
  // causing Tab B to dispatch setUserActiveSession with a now-invalid session — bypassing license
  // and RBAC checks. The cancelled flag prevents the stale .then() dispatch if this component
  // unmounts between the network call starting and its response arriving.
  useEffect(() => {
    let cancelled = false;
    setupResponseInterceptor(dispatch);
    if (reducerActiveSession || activeSessionCookie) {
      validateSessionApi(reducerActiveSession ? JSON.stringify(reducerActiveSession) : activeSessionCookie)
        .then((response) => {
          if (cancelled) return;
          if (response?.active === false) {
            removeToken(COOKIE_ACTIVE_SESSION, { path: COOKIE_PATH });
            invalidateTokenApi(reducerActiveSession ? reducerActiveSession : JSON.parse(activeSessionCookie));
            if (!window.location.pathname.includes(ROUTES.LOGIN)) {
              window.location.replace(window.location.origin + ROUTES.LOGIN);
            } else {
              setLoginStatus(userLoggedIn.FAILED);
            }
            return;
          } else {
            const sessionToParse = reducerActiveSession ? reducerActiveSession : JSON.parse(activeSessionCookie);
            const isRedisEnabled = window.REACT_APP_USE_REDIS_SESSION?.trim().toLowerCase() === 'true';
            // Fix: Purge stale Redis-mode activeSession cookie when Redis is disabled.
            // If a previous deployment had Redis enabled, the activeSession cookie may persist with
            // isRedisMode:true. When Redis is later disabled, dispatching that stale session to Redux
            // would cause all API calls to omit the Bearer token. Clear the cookie and force re-login instead.
            if (sessionToParse?.isRedisMode && !isRedisEnabled) {
              removeToken(COOKIE_ACTIVE_SESSION, { path: COOKIE_PATH });
              setLoginStatus(userLoggedIn.FAILED);
              return;
            }
            dispatch(
              setUserActiveSession({
                activeSession: sessionToParse
              })
            );
          }
        })
        .catch((err) => {
          if (!cancelled) setLoginStatus(userLoggedIn.FAILED);
        });
    }
    return () => {
      cancelled = true;
    };
  }, []);

  const renderPage = () => {
    switch (loginStatus) {
      case userLoggedIn.SUCCESS:
        return <RootLayoutV2 />;
      case userLoggedIn.FAILED:
        return <Login />;
      case userLoggedIn.LOADING:
        return <Loader active={true} />;
      default:
        return <Login />;
    }
  };

  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route>
          <Route path="/login" element={<Login />} />
          <Route path="/forgotPassword" element={<ForgotPassword />} />
          <Route path="/resetPassword" element={<PasswordReset />} />
          <Route path="/userRegistration" element={<UserRegistration />} />

          <Route path={"/"} element={renderPage()}>
            <Route path="" element={<Navigate to="/dashboard" replace />} />
            <Route path="loginsso" element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="devices" element={<NewDevices />} />
            <Route path="companionDeviceInfoPage" element={<CompanionDeviceInfo />} />
            <Route path="cameraInfoPage" element={<CameraInfo />} />
            <Route path="roi" element={<RegionOfInterest />} />
            <Route path="alerts" element={<Alerts />} />
            <Route path="createRuleSummary" element={<CreateRuleSummary />} />
            <Route path="usergroups" element={<UserGroups />} />
            <Route path="userdetails" element={<UserDetails />} />
            <Route path="usergroupsdetails" element={<UserGroupsDetails />} />
            <Route path="location" element={<Location />} />
            <Route path="events" element={<Incidents />}></Route>
            <Route path="search" element={isFeatureAccessible(isSearchEnabled, isV1OnlyAccount) ? <HistoricalSearch /> : <Navigate to={ROUTES.DASHBOARD} replace />} ></Route>
            <Route path="searchResult" element={isFeatureAccessible(isSearchEnabled, isV1OnlyAccount) ? <SearchResult /> : <Navigate to={ROUTES.DASHBOARD} replace />} ></Route>
            <Route path="eventsInfo" element={<EventsInfo />} />
            <Route path="cvapps" element={<CVApps />} />
            <Route path="createCVApps" element={<CreateCVApps />} />
            <Route path="settings" element={<Settings />} />
            <Route path="sites" element={<Sites />} />
            <Route path="support" element={<Support />} />
            <Route path="addsite" element={<EditSite />} />
            <Route path="editsite" element={<EditSite />} />
            <Route path="cameraOnboarding" element={<CameraOnboarding />} />
            <Route path="statistics" element={<StatisticsComponent />} />
            <Route path="campusDetails" element={<CampusDetails />} />
            <Route path="roles" element={<Roles />} />
            <Route path="editRole" element={<EditRoleTemplate />} />
            <Route path="createRole" element={<CreateRole />} />
            <Route path="bulkRule" element={<BulkRule />} />
            <Route path="createAlert" element={<CreateRule />} />
            <Route path="ruleModal" element={<RoiModal />} />
            <Route path="statDataSource" element={<StatDataSource />} />
            <Route path="alertDetails" element={<RuleDetails />} />
            <Route path="licenses" element={<Licenses />} />
            <Route path="licensedetails" element={<LicenseDetailsRoute />} />
            <Route path="manualcalibration" element={<StreamManualCalibration />} />
            
            {/* Appearance routes */}
            <Route path="enrolledAppearance" element={isFeatureAccessible(isEaEnabled, isV1OnlyAccount) ? <EAList /> : <Navigate to={ROUTES.DASHBOARD} replace />} />
            <Route path="createAppearance" element={isFeatureAccessible(isEaEnabled, isV1OnlyAccount) ? <EACreate /> : <Navigate to={ROUTES.DASHBOARD} replace />} />
            <Route path="editAppearance" element={isFeatureAccessible(isEaEnabled, isV1OnlyAccount) ? <EACreate /> : <Navigate to={ROUTES.DASHBOARD} replace />} />
            <Route path="appearanceDeployments" element={isFeatureAccessible(isEaEnabled, isV1OnlyAccount) ? <AssociatedStreams /> : <Navigate to={ROUTES.DASHBOARD} replace />} />
            
            <Route path="users" element={<Users />} />
            <Route path="UserSettings" element={<UserSettings />} />
            <Route path="cvAppSub" element={<CVAppSub />} />
            <Route path="cvAppsLicenseType" element={<CVAppsLicenseType />} />
            <Route path="polygon" element={<AnnotationDraw />} />
            <Route path="assistant" element={isNluEnabled ? <Assistant /> : <Navigate to={ROUTES.DASHBOARD} replace />} />
            <Route path="*" element={<ErrorPage />} />
          </Route>

          <Route path="*" element={<ErrorPage />} />
        </Route>
      </>
    ),
    { basename: window.REACT_APP_DOMAIN_SMARTVISION }
  );

  // License modal takes priority over role error:
  // A user without a license often also has no role — showing the license error is more actionable.
  // Suppress role error until license check completes so the correct modal wins.
  if (roleError && licensesLoaded && billingAccountsLoaded && !showNoLicenseModal) {
    return (
      <AccessDeniedModal
        message={roleError}
        onLogout={handleAccessDeniedLogout}
        onOk={handleAccessDeniedOk}
        isLoggingOut={isLoggingOut}
      />
    );
  }

  if (showNoLicenseModal) {
    const billingAccountId = billingAccounts && billingAccounts.length > 0 ? billingAccounts[0] : "";

    const otherApps = [];
    if (Array.isArray(otherAppLicenses) && otherAppLicenses.length > 0) {
      otherAppLicenses.forEach((license) => {
        if (license?.sku && license?.licenses >= 1) {
          const appName = SKU_TO_APP_MAPPING[license.sku];
          if (appName && !otherApps.includes(appName)) {
            otherApps.push(appName);
          }
        }
      });
    }

    let modalMessage = "";
    if (ecpdId && billingAccountId) {
      const ecpdDisplay = ecpdAccountName ? `${ecpdAccountName} (${ecpdId})` : ecpdId;
      modalMessage = NO_LICENSE_MESSAGES.WITH_ACCOUNT(accountEmail, ecpdDisplay, billingAccountId);
    } else {
      modalMessage = NO_LICENSE_MESSAGES.WITHOUT_ACCOUNT(accountEmail);
    }

    return (
      <AccessDeniedModal
        message={modalMessage}
        otherApps={otherApps}
        onLogout={handleAccessDeniedLogout}
        onOk={handleAccessDeniedOk}
        isLoggingOut={isLoggingOut}
      />
    );
  }

  return (
    <>
      <LicenseOveruseModal isOpen={showOveruseModal} onNavigate={handleManageLicenses} />
      <Suspense fallback={<PageLoadingFallback />}>
        <IdleTimeModal />
        <RouterProvider router={router} />
      </Suspense>
    </>
  );
}
export default NewApp;